import type { MigrationManifest, PersistedState } from "redux-persist";
import {
  createMigrate,
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE
} from "redux-persist";

import { combineReducers, configureStore } from "@reduxjs/toolkit";

import localforage from "localforage";

import { analysisJobReducer, resetAnalysisJob } from "./slice/analysisJob";
import {
  analysisSummaryReducer,
  resetAnalysisSummaryState
} from "./slice/analysisSummary";
import { authReducer, resetAuth } from "./slice/authSlice";
import type { DatasetState } from "./slice/datasetSlice";
import { datasetReducer, resetDatasetState } from "./slice/datasetSlice";
import {
  globalFilterReducer,
  GlobalFilterState,
  resetGlobalFilter
} from "./slice/globalFilterSlice";
import { mapReducer, resetMapState } from "./slice/mapSlice";
import { resetSpatialState, spatialReducer } from "./slice/spatialSlice";
import { resetUIDataConfig, uiReducer } from "./slice/uiSlice";

localforage.config({
  name: "lokasi-intelligence",
  storeName: "lokasi_intelligence_store"
});

type MigrationManifestTyped<S> = {
  [version: number]: (
    state: (S & PersistedState) | undefined
  ) => S | undefined | Promise<S | undefined>;
};

type CreatePersistConfigParams<S> = {
  key: string;
  version: number;
  storage: LocalForage;
  migrations?: MigrationManifestTyped<S>;
  whitelist?: string[];
  blacklist?: string[];
};

export const createPersistConfig = <S>({
  key,
  version,
  storage,
  migrations = {},
  whitelist,
  blacklist
}: CreatePersistConfigParams<S>) => {
  const baseMigrate = createMigrate(migrations as MigrationManifest, {
    debug: import.meta.env.MODE === "development"
  });

  const migrate = async (state: PersistedState, currentVersion: number) => {
    const fromVersion = state?._persist?.version ?? 0;

    if (fromVersion >= currentVersion) {
      return state;
    }

    return baseMigrate(state, currentVersion);
  };

  return {
    key,
    version,
    storage,
    whitelist,
    blacklist,
    migrate: Object.keys(migrations).length ? migrate : undefined
  };
};

const mapPersistConfig = createPersistConfig({
  key: "map",
  version: 0,
  storage: localforage,
  blacklist: ["selectedFeature"]
});

const spatialPersistConfig = createPersistConfig({
  key: "spatial",
  version: 0,
  storage: localforage,
  blacklist: [
    "catchmentSettingsTemp",
    "adminAreaSettingsDataTemp",
    "adminTreeSettingsTemp"
  ]
});

const datasetPersistConfig = createPersistConfig<DatasetState>({
  key: "dataset",
  version: 0,
  storage: localforage
});

const analysisSummaryPersistConfig = createPersistConfig({
  key: "analysisSummary",
  version: 0,
  storage: localforage,
  blacklist: ["gridsSelected"]
});

const uiPersistConfig = createPersistConfig({
  key: "ui",
  version: 0,
  storage: localforage,
  blacklist: [
    "showPolygonPopup",
    "allowPinMarker",
    "showAreaAdminPopup",
    "showCatchmentPopup",
    "showDatasetPopup",
    "showDataWeightSettingsPopup",
    "showSpatialAnalysisResultTabularModal",
    "sideBarMenuTypePopupDisplay",
    "isExpandLegendVisualization",
    "showProjectList",
    "showLegendInfo",
    "sidebarUserSettingPopupDisplay",
    "showSearchLocationPopup",
    "markerInfo",
    "showGlobalFilterPopup",
    "showLoadingOverlay"
  ]
});

const authPersistConfig = createPersistConfig({
  key: "auth",
  version: 0,
  storage: localforage
});

const analysisJobPersistConfig = createPersistConfig({
  key: "analysisJob",
  version: 0,
  storage: localforage
});

const globalFilterPersistConfig = createPersistConfig({
  key: "globalFilter",
  version: 1,
  storage: localforage,
  blacklist: ["adminAreaSettingsDataTemp", "selectedAreaIdsTemp"],
  migrations: {
    /**
     * Version 1: Remove LinkedDatasetID from globalFilter state as it is no longer needed after the dataset state has been migrated to remove linkedDatasetID.
     * This migration will ensure that any existing persisted state that still contains linkedDatasetID will have it removed.
     */
    1: async (state) => {
      /**
       * TODO: Research is needed regarding the migration process to move data from the linkedDatasetID into the dataset after the linkedDatasetID is deleted. ,
       * In cases where the user takes no action, the migration has not been successful
       * Ticket: @see https://bvarta-project.atlassian.net/browse/ORB-2965
       */
      const legacyState = state as
        | (GlobalFilterState & { linkedDatasetIds?: string[] })
        | undefined;

      if (!legacyState) return state;

      delete legacyState.linkedDatasetIds;
      return legacyState;
    }
  }
});

export const rootReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authReducer),
  map: persistReducer(mapPersistConfig, mapReducer),
  dataset: persistReducer(datasetPersistConfig, datasetReducer),
  spatial: persistReducer(spatialPersistConfig, spatialReducer),
  ui: persistReducer(uiPersistConfig, uiReducer),
  analysisSummary: persistReducer(
    analysisSummaryPersistConfig,
    analysisSummaryReducer
  ),
  analysisJob: persistReducer(analysisJobPersistConfig, analysisJobReducer),
  globalFilter: persistReducer(globalFilterPersistConfig, globalFilterReducer)
});

export const store = configureStore({
  devTools: import.meta.env.MODE !== "production",
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Actions from redux-persist are ignored because they may contain non-serializable values
        // and are essential for the persistence mechanism to work correctly.
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    })
});

export const persistor = persistStore(store);

// Function to reset the store
// We can custom the store we want to reset
export const resetStore = async () => {
  store.dispatch(resetAnalysisSummaryState());
  store.dispatch(resetAuth());
  store.dispatch(resetDatasetState());
  store.dispatch(resetMapState());
  store.dispatch(resetSpatialState());
  store.dispatch(resetUIDataConfig());
  store.dispatch(resetAnalysisJob());
  store.dispatch(resetGlobalFilter());
};

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
